import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

type ReadingMode = 'scroll' | 'flip' | 'popup';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const [comic, setComic] = useState<Comic | null>(null);
  const [readingMode, setReadingMode] = useState<ReadingMode>('scroll');
  const [showReader, setShowReader] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = 8;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const found = comics.find((c) => c.id === Number(id));
    setComic(found || null);
  }, [id]);

  if (!comic) {
    return (
      <main className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
          <p className="text-foreground-500 mt-4 text-lg">漫画未找到</p>
          <Link to="/" className="inline-block mt-4 text-primary-500 hover:text-primary-600">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const readingPages = [
    { pageNum: 1, imgUrl: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20comic%20page%201%2C%20vertical%20scroll%20format%2C%20two%20high%20school%20students%20talking%20in%20classroom%2C%20dynamic%20panel%20layout%2C%20speech%20bubbles%2C%20clean%20line%20art%20with%20screen%20tones%2C%20black%20and%20white%20manga%20style%2C%20professional%20illustration&width=800&height=1150&seq=reader-p1&orientation=portrait' },
    { pageNum: 2, imgUrl: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20comic%20page%202%2C%20close%20up%20of%20female%20character%20blushing%2C%20romantic%20scene%2C%20vertical%20scroll%20format%2C%20speech%20bubbles%2C%20clean%20line%20art%20with%20screen%20tones%2C%20black%20and%20white%20manga%20style%2C%20professional%20illustration&width=800&height=1150&seq=reader-p2&orientation=portrait' },
    { pageNum: 3, imgUrl: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20comic%20page%203%2C%20male%20character%20walking%20in%20school%20hallway%2C%20dramatic%20angle%2C%20vertical%20scroll%20format%2C%20speech%20bubbles%2C%20clean%20line%20art%20with%20screen%20tones%2C%20black%20and%20white%20manga%20style&width=800&height=1150&seq=reader-p3&orientation=portrait' },
    { pageNum: 4, imgUrl: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20comic%20page%204%2C%20two%20characters%20meeting%20at%20school%20gate%2C%20cherry%20blossom%20background%2C%20romantic%20atmosphere%2C%20vertical%20scroll%20format%2C%20speech%20bubbles%2C%20black%20and%20white%20manga%20style&width=800&height=1150&seq=reader-p4&orientation=portrait' },
    { pageNum: 5, imgUrl: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20comic%20page%205%2C%20characters%20studying%20together%20in%20library%2C%20intimate%20moment%2C%20vertical%20scroll%20format%2C%20speech%20bubbles%2C%20clean%20line%20art%20with%20screen%20tones%2C%20black%20and%20white%20manga%20style&width=800&height=1150&seq=reader-p5&orientation=portrait' },
    { pageNum: 6, imgUrl: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20comic%20page%206%2C%20dramatic%20confrontation%20scene%20on%20rooftop%2C%20wind%20blowing%2C%20emotional%20moment%2C%20vertical%20scroll%20format%2C%20speech%20bubbles%2C%20black%20and%20white%20manga%20style&width=800&height=1150&seq=reader-p6&orientation=portrait' },
    { pageNum: 7, imgUrl: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20comic%20page%207%2C%20couple%20holding%20hands%20walking%20at%20sunset%2C%20romantic%20climax%2C%20vertical%20scroll%20format%2C%20speech%20bubbles%2C%20black%20and%20white%20manga%20style%2C%20beautiful%20scene&width=800&height=1150&seq=reader-p7&orientation=portrait' },
    { pageNum: 8, imgUrl: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20comic%20page%208%2C%20characters%20looking%20at%20each%20other%20with%20determination%2C%20final%20panel%2C%20to%20be%20continued%20text%2C%20vertical%20scroll%20format%2C%20speech%20bubbles%2C%20black%20and%20white%20manga%20style&width=800&height=1150&seq=reader-p8&orientation=portrait' },
  ];

  return (
    <main className="min-h-screen pt-16">
      {!showReader ? (
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb items={[
            { label: '首页', to: '/' },
            { label: comic.tags[0] ? `${comic.tags[0]}漫画` : '漫画分类', to: `/category/${comic.category}` },
            { label: comic.title },
          ]} />

          <div className="flex flex-col md:flex-row gap-8 mt-6">
            <div className="md:w-64 flex-shrink-0">
              <div className="rounded-xl overflow-hidden bg-background-100">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - 香蕉文化漫画网`}
                  title={`${comic.title} - 免费在线漫画阅读`}
                  className="w-full aspect-[3/4] object-cover object-top"
                />
              </div>
              <button
                onClick={() => setShowReader(true)}
                className="w-full mt-4 bg-primary-500 hover:bg-primary-600 text-white py-3 rounded-full text-sm font-semibold transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-open-line"></i>
                开始阅读
              </button>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">{comic.title}</h1>
              <p className="text-sm text-foreground-500 mt-2">作者：{comic.author}</p>

              <div className="flex flex-wrap items-center gap-3 mt-4">
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-amber-400"></i>
                  <span className="text-lg font-bold text-foreground-950">{comic.rating}</span>
                  <span className="text-xs text-foreground-500">分</span>
                </div>
                <span className="text-foreground-300">|</span>
                <span className="text-sm text-foreground-600">
                  {(comic.views / 10000).toFixed(0)}万阅读
                </span>
                <span className="text-foreground-300">|</span>
                <span className={`text-sm font-medium ${comic.status === '连载中' ? 'text-secondary-600' : 'text-accent-600'}`}>
                  {comic.status}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {comic.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category/${comic.category}`}
                    className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs hover:bg-accent-200 transition-colors whitespace-nowrap"
                  >
                    {tag}
                  </Link>
                ))}
              </div>

              <div className="mt-6 bg-background-100 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-foreground-950 mb-2">作品简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="mt-6">
                <h3 className="text-sm font-semibold text-foreground-950 mb-3">
                  章节列表（共 {comic.chapters.length} 话）
                </h3>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {comic.chapters.map((chapter) => (
                    <button
                      key={chapter.id}
                      onClick={() => { setShowReader(true); setCurrentPage(1); }}
                      className="w-full flex items-center justify-between bg-background-100 hover:bg-background-200 rounded-lg px-4 py-3 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        {chapter.isFree ? (
                          <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                            免费
                          </span>
                        ) : (
                          <span className="text-xs bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                            VIP
                          </span>
                        )}
                        <span className="text-sm text-foreground-700">{chapter.title}</span>
                      </div>
                      <span className="text-xs text-foreground-400">{chapter.updatedAt}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6 sticky top-16 bg-background-50/95 backdrop-blur-sm z-40 py-3">
            <button
              onClick={() => setShowReader(false)}
              className="flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> 返回详情
            </button>
            <h2 className="text-sm font-semibold text-foreground-950 truncate max-w-[200px]">{comic.title}</h2>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {([
                { mode: 'scroll', icon: 'ri-arrow-down-line', label: '下拉式' },
                { mode: 'flip', icon: 'ri-book-open-line', label: '翻页' },
                { mode: 'popup', icon: 'ri-window-line', label: '弹窗' },
              ] as const).map((m) => (
                <button
                  key={m.mode}
                  onClick={() => setReadingMode(m.mode)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    readingMode === m.mode
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-950'
                  }`}
                >
                  <i className={m.icon}></i>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {readingMode === 'popup' ? (
            <div className="bg-black/70 fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="bg-background-50 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-background-200/70">
                  <span className="text-sm font-semibold text-foreground-950">
                    第 {currentPage} / {totalPages} 页
                  </span>
                  <button
                    onClick={() => setShowReader(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-lg"></i>
                  </button>
                </div>
                <div className="overflow-y-auto max-h-[70vh] p-4 flex justify-center">
                  <img
                    src={readingPages[currentPage - 1]?.imgUrl}
                    alt={`${comic.title} 第${currentPage}页`}
                    className="max-w-full rounded-lg"
                  />
                </div>
                <div className="flex items-center justify-center gap-4 p-4 border-t border-background-200/70">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage <= 1}
                    className="px-4 py-2 rounded-lg bg-background-100 hover:bg-background-200 text-sm disabled:opacity-40 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    上一页
                  </button>
                  <span className="text-sm text-foreground-500">{currentPage} / {totalPages}</span>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage >= totalPages}
                    className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm disabled:opacity-40 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    下一页
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {readingMode === 'flip' && (
                <div className="flex items-center justify-center gap-4 mb-4">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage <= 1}
                    className="px-5 py-2 rounded-full bg-background-100 hover:bg-background-200 text-sm disabled:opacity-40 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line mr-1"></i>上一页
                  </button>
                  <span className="text-sm text-foreground-500 font-medium">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage >= totalPages}
                    className="px-5 py-2 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-sm disabled:opacity-40 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    下一页<i className="ri-arrow-right-line ml-1"></i>
                  </button>
                </div>
              )}

              <div className={readingMode === 'flip' ? '' : 'space-y-0'}>
                {readingMode === 'scroll' ? (
                  readingPages.map((page) => (
                    <div key={page.pageNum} className="flex justify-center">
                      <img
                        src={page.imgUrl}
                        alt={`${comic.title} 第${page.pageNum}页`}
                        className="w-full max-w-2xl rounded-lg"
                        loading="lazy"
                      />
                    </div>
                  ))
                ) : (
                  <div className="flex justify-center">
                    <img
                      src={readingPages[currentPage - 1]?.imgUrl}
                      alt={`${comic.title} 第${currentPage}页`}
                      className="w-full max-w-2xl rounded-lg"
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": `${comic.title} - 香蕉文化漫画网`,
            "author": { "@type": "Person", "name": comic.author },
            "datePublished": comic.updatedAt,
            "description": comic.description,
            "url": `https://www.xiangjiaowenhua.com/comic/${comic.id}`,
            "publisher": {
              "@type": "Organization",
              "name": "香蕉文化漫画网"
            }
          })
        }}
      />
    </main>
  );
}